/**
 * MasterPatternAdapter
 *
 * Implementa la interfaz CrudService<T> consumiendo los endpoints estándar
 * generados por la librería Siesa.MasterPattern (.NET).
 *
 * Endpoints asumidos:
 *   POST   /{resourcePath}/search  → SearchResponse (getAll)
 *   GET    /{resourcePath}/{id}    → T              (getById)
 *   POST   /{resourcePath}         → T              (create)
 *   PUT    /{resourcePath}/{id}    → T              (update)
 *   DELETE /{resourcePath}/{id}    → 204            (delete / deactivate)
 *
 * Normalización de claves:
 *   C# serializa en PascalCase (System.Text.Json) → convertimos a camelCase JS.
 *   Ej: "ExchangeRate" → "exchangeRate", "ID" → "id", "UUID" → "uuid".
 */

import type {
  CrudService,
  GetAllParams,
  GetAllResponse,
  MasterPatternConfig,
  MasterCrudOperationType,
  AdvancedFilter,
  MasterCrudField,
} from './MasterCrud.types';

// ============================================================
// Utilidades de normalización de claves
// ============================================================

/** PascalCase/ALL_CAPS → camelCase */
const toCamelCase = (key: string): string => {
  if (!key) return key;
  // Palabras completamente en mayúsculas (ID, UUID, CODE, etc.) → minúsculas
  if (/^[A-Z0-9]+$/.test(key)) return key.toLowerCase();
  // Primer carácter a minúscula
  return key.charAt(0).toLowerCase() + key.slice(1);
};

/** camelCase → PascalCase (para enviar en SearchRequest) */
const toPascalCase = (key: string): string => {
  if (!key) return key;
  return key.charAt(0).toUpperCase() + key.slice(1);
};

/** Convierte todas las claves de un objeto plano a camelCase */
const normalizeResponseKeys = <T>(raw: Record<string, any>): T => {
  const result: Record<string, any> = {};
  for (const key of Object.keys(raw)) {
    result[toCamelCase(key)] = raw[key];
  }
  return result as T;
};

// ============================================================
// Error helper
// ============================================================

async function throwIfNotOk(response: Response): Promise<never> {
  let message = `HTTP ${response.status}`;
  try {
    const body = await response.json();
    // Prioridad: message → detail (RFC 7807) → title → string literal
    // `detail` contiene el mensaje legible ("El código debe ser único")
    // `title` contiene el código técnico ("validation.validation_failed")
    if (body?.message) message = body.message;
    else if (body?.detail) message = body.detail;
    else if (body?.title) message = body.title;
    else if (typeof body === 'string') message = body;
  } catch {
    // ignore parse error
  }
  throw new Error(message);
}

// ============================================================
// Conversión de filtros avanzados → JsonElement[][] (AND of OR groups)
// ============================================================

/**
 * Convierte la estructura de AdvancedFilter del frontend al formato
 * JsonElement[][] que espera SearchRequest.filters en el backend.
 *
 * Regla de conversión:
 *   - Un nodo hoja { field, operator, value } → elemento simple  { field, operator, value }
 *   - Grupos raíces con connector "OR" se agrupan en el mismo array interno
 *   - Grupos con connector "AND" generan un array separado (nueva dimensión)
 *
 * Formato resultante: [[filter1, filter2], [filter3]] donde el AND está entre
 * arrays y el OR está dentro del mismo array.
 */
function convertAdvancedFilters(filters: AdvancedFilter[]): Record<string, any>[][] {
  const andGroups: Record<string, any>[][] = [];

  const toLeaf = (f: AdvancedFilter) => ({
    field: toPascalCase(f.field as string),
    operator: f.operator,
    value: f.value,
  });

  for (const filter of filters) {
    if (filter.isGroup && filter.children?.length) {
      if (filter.connector === 'OR') {
        // OR entre hijos → todos en el MISMO array interno (una sola dimensión).
        const orLeaves = filter.children
          .filter((c) => c.field && c.operator !== undefined)
          .map(toLeaf);
        if (orLeaves.length) andGroups.push(orLeaves);
      } else {
        // AND entre hijos → cada uno en su propio array (nueva dimensión).
        const group = convertAdvancedFilters(filter.children);
        andGroups.push(...group);
      }
    } else if (filter.field && filter.operator !== undefined) {
      andGroups.push([toLeaf(filter)]);
    }
  }

  return andGroups;
}

// ============================================================
// Adaptador principal
// ============================================================

export class MasterPatternCrudAdapter<T extends Record<string, any>>
  implements CrudService<T>
{
  private readonly config: MasterPatternConfig;
  private readonly fields: MasterCrudField<T>[];

  constructor(config: MasterPatternConfig, fields: MasterCrudField<T>[] = []) {
    this.config = config;
    this.fields = fields;
  }

  // ----------------------------------------------------------
  // URL helpers
  // ----------------------------------------------------------

  private getBaseUrl(): string {
    const base = this.config.baseUrl.replace(/\/$/, '');
    const path = this.config.resourcePath.replace(/^\//, '').replace(/\/$/, '');
    return `${base}/${path}`;
  }

  private getSearchUrl(): string {
    return `${this.getBaseUrl()}/search`;
  }

  private getItemUrl(id: string | number): string {
    return `${this.getBaseUrl()}/${id}`;
  }

  // ----------------------------------------------------------
  // Header builder (incluye X-Company-ID cuando corresponde)
  // ----------------------------------------------------------

  private async buildHeaders(companyData?: any): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };

    if (this.config.getHeaders) {
      const custom = await this.config.getHeaders();
      Object.assign(headers, custom);
    }

    // X-Company-ID: se omite si uuid es '000' (Global) o no está definido
    const uuid = companyData?.uuid;
    if (uuid && String(uuid) !== '000') {
      headers['X-Company-ID'] = String(uuid);
    }

    return headers;
  }

  // ----------------------------------------------------------
  // Campos a solicitar en SearchRequest.fields
  // ----------------------------------------------------------

  private getRequestFields(): string[] | undefined {
    if (this.config.requestFields?.length) {
      return this.config.requestFields;
    }
    // Auto-deriva desde la definición de campos del MasterCrud
    const keys = this.fields
      .map((f) => f.accessorKey as string)
      .filter(Boolean)
      .map(toPascalCase);
    return keys.length ? keys : undefined;
  }

  // ----------------------------------------------------------
  // SearchRequest builder
  // ----------------------------------------------------------

  private buildSearchBody(params: GetAllParams, companyData?: any): Record<string, any> {
    if (this.config.mapSearchRequest) {
      return this.config.mapSearchRequest(params, companyData);
    }

    const body: Record<string, any> = {
      page: params.page,
      pageSize: params.limit,
    };

    const fields = this.getRequestFields();
    if (fields?.length) body.fields = fields;

    if (params.search) {
      body.search = params.search;
      if (params.searchField) {
        body.searchFields = [toPascalCase(params.searchField as string)];
      }
    }

    if (params.sortBy) {
      body.orderBy = {
        field: toPascalCase(params.sortBy as string),
        direction: params.sortDirection ?? 'asc',
      };
    }

    const advancedFilters = params.filters?.advancedFilters;
    if (Array.isArray(advancedFilters) && advancedFilters.length > 0) {
      const converted = convertAdvancedFilters(advancedFilters);
      if (converted.length > 0) body.filters = converted;
    }

    // Alcance de filas en modo sobrescritura multicompañía (aditivo). El backend
    // Siesa.MasterPattern puede usarlo para acotar a habilitados en la compañía.
    if (params.filters?.viewScope) {
      body.viewScope = params.filters.viewScope;
    }

    return body;
  }

  // ----------------------------------------------------------
  // CrudService implementation
  // ----------------------------------------------------------

  async getAll(params: GetAllParams, companyData?: any): Promise<GetAllResponse<T>> {
    const headers = await this.buildHeaders(companyData);
    const body = this.buildSearchBody(params, companyData);

    const response = await fetch(this.getSearchUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });

    if (!response.ok) await throwIfNotOk(response);

    const json = await response.json();
    // SearchResponse: { data: Dictionary<string, object?>[], page, pageSize }
    const rawItems: Record<string, any>[] = json.data ?? json.Data ?? [];

    // Normaliza claves PascalCase → camelCase
    const normalizedData = rawItems.map((item) => normalizeResponseKeys<T>(item));

    // Heurística hasMore: si llegaron pageSize registros, podrían haber más
    const limit = params.limit;
    const knownMin = (params.page - 1) * limit + normalizedData.length;
    const mightHaveMore = normalizedData.length >= limit;
    const total = mightHaveMore ? knownMin + 1 : knownMin;

    return { data: normalizedData, total };
  }

  async getById(id: string | number, companyData?: any): Promise<T> {
    const headers = await this.buildHeaders(companyData);
    const response = await fetch(this.getItemUrl(id), { method: 'GET', headers });
    if (!response.ok) await throwIfNotOk(response);
    const raw: Record<string, any> = await response.json();
    return normalizeResponseKeys<T>(raw);
  }

  async create(data: Partial<T>, companyData?: any): Promise<T> {
    const headers = await this.buildHeaders(companyData);
    const response = await fetch(this.getBaseUrl(), {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) await throwIfNotOk(response);
    const raw: Record<string, any> = await response.json();
    return normalizeResponseKeys<T>(raw);
  }

  async update(id: string | number, data: Partial<T>, companyData?: any): Promise<T> {
    const headers = await this.buildHeaders(companyData);
    const response = await fetch(this.getItemUrl(id), {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    if (!response.ok) await throwIfNotOk(response);
    const raw: Record<string, any> = await response.json();
    return normalizeResponseKeys<T>(raw);
  }

  /**
   * DELETE /{resourcePath}/{id}
   *
   * En modo MasterPattern el endpoint DELETE solo desactiva (soft-delete).
   * La operación 'activate' no existe → se ignora silenciosamente.
   * MasterCrud.tsx fuerza effectiveAllowStatusReversion=false para que el
   * botón de activación nunca aparezca.
   */
  async delete(
    id: string | number,
    companyData?: any,
    operation?: MasterCrudOperationType,
  ): Promise<void> {
    // 'activate' no tiene endpoint — no-op (el botón no debería aparecer en UI)
    if (operation === 'activate') return;

    const headers = await this.buildHeaders(companyData);
    const response = await fetch(this.getItemUrl(id), {
      method: 'DELETE',
      headers,
    });

    // 204 No Content = éxito
    if (response.status === 204 || response.ok) return;
    await throwIfNotOk(response);
  }
}
